import { GetStaticProps } from 'next';
import Layout from '@/components/layout';
import { configuredSanityClient } from '@/lib/sanity/sanityClient';
import { getNavigation } from '@/lib/sanity/queries';
import { ArticleCard, ArticleCardProps, CategoryHero } from '@fagmobler/ui';
import Image from 'next/image';
import Debug from '@/components/debug';
import groq from 'groq';
import {
  imageWithCropProjection,
  MaterializedArticleCard,
  MaterializedImageWithCrop,
  Page,
} from '@fagmobler/sanity';
import { fagmoblerSlugify } from '@/lib/utils/getUrls';
import Link from 'next/link';

function truncate(input: string = '', length: number) {
  if (!input) return input;
  if (input.length > length) {
    return input.substring(0, length) + '...';
  }
  return input;
}

type SimpleMaterilizedPage = {
  _id: Page['_id'];
  title?: Page['title'];
  slug?: Page['slug'];
  lead?: Page['lead'];
  mainImage: MaterializedImageWithCrop;
  description?: string;
};

const simpleInspirasjonPageQuery = groq`
  *[_type == "page" && category == 'inspirasjon' && !(_id in path('drafts.**'))] {
    _id,
    title,
    slug,
    lead,
    orderRank,
    defined(lead) => {
      "description": pt::text(lead)
    },
    !defined(lead) && defined(body) => {
      "description": pt::text(body[0..2])
    },
    mainImage {
      ${imageWithCropProjection}
    }
  } | order(orderRank)
`;

export const getStaticProps: GetStaticProps = async (context) => {
  const navigation = await getNavigation();
  const { preview = false, previewData = null, params } = context;

  const pages = await configuredSanityClient
    .fetch<SimpleMaterilizedPage[]>(simpleInspirasjonPageQuery)
    .then(async (data) => {
      // if (data) return await populateWithImageUrl(data);
      return data;
    });

  if (!pages) {
    return {
      revalidate: 60 * 60,
      notFound: true,
      props: {
        navigation,
      },
    };
  }

  return {
    props: {
      navigation,
      pages,
    },
    revalidate: 60 * 60,
  };
};

const InspirasjonCard = ({
  page,
  card,
  body,
  size,
  direction,
}: {
  page: SimpleMaterilizedPage;
  card?: MaterializedArticleCard;
  body?: string;
  size?: ArticleCardProps['size'];
  direction?: ArticleCardProps['direction'];
}) => {
  if (page.slug?.current && page.mainImage) {
    let imageSizes =
      '(max-width: 640px) 100vw, (max-width: 1024px) 336px, (max-width: 1256px) 432px, 656px';
    if (size === 'lg')
      imageSizes = '(max-width: 640px) 50vw, (max-width: 1024px) 336px, 656px';

    return (
      <Link href={`/inspirasjon/${fagmoblerSlugify(page.slug.current)}`}>
        <ArticleCard
          size={size || 'md'}
          title={page.title}
          body={body}
          direction={direction || 'horizontal'}
          image={
            page.mainImage.croppedImageUrl && (
              <Image
                src={page.mainImage.croppedImageUrl}
                alt={page.mainImage?.alt || page.title || ''}
                width={page.mainImage?.width || 0}
                height={page.mainImage?.height || 0}
                placeholder={page.mainImage?.lqip ? 'blur' : undefined}
                blurDataURL={page.mainImage?.lqip || undefined}
                sizes={imageSizes}
              />
            )
          }
          {...card}
        />
      </Link>
    );
  }
  return null;
};

const InspirasjonPage = ({ pages }: { pages: SimpleMaterilizedPage[] }) => {
  return (
    <Layout isPreview={false}>
      <div className="my-16">
        <CategoryHero title="Inspirasjon" />
      </div>
      <div className="space-y-5">
        {/* First large card */}
        <InspirasjonCard
          page={pages[0]}
          size="lg"
          body={truncate(pages[0]?.description, 250)}
        />

        {/* Two medium cards */}
        <div className="grid md:grid-cols-2 gap-5">
          {pages.slice(1, 3).map((page) => {
            if (!page.slug?.current) return null;
            return (
              <InspirasjonCard
                page={page}
                key={page._id}
                size="lg"
                direction="vertical"
                body={truncate(page.description, 200)}
              />
            );
          })}
        </div>

        {/* Rest of cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {pages.map((page, i) => {
            if (!page.slug?.current || i < 3) return null;
            return (
              <InspirasjonCard
                page={page}
                size="md"
                key={page._id}
                direction="vertical"
                body={truncate(page.description, 70)}
              />
            );
          })}
        </div>
      </div>
      <Debug data={pages} label="Pages" />
    </Layout>
  );
};

export default InspirasjonPage;
